import Immutable from 'immutable';
import { createImmutableMap } from 'ctm-util-functions';

/**
 * Get the state position of a filter
 * @param {String} filter - The action object
 * @param {Boolean} filterStaged - The action object
 * @returns {Array} The array state position
 */
const getFilterPosition = (filter, filterStaged) => {
    const statePosition = ['config', 'filters', filter];
    if (filterStaged === true) {
        statePosition.splice(1, 0, 'helpers');
    }
    return statePosition;
};
/**
 * Set the filter data to state for an individual filter
 * @param {Immutable.Map} state - The App State
 * @param {String} filter - The action object
 * @param {Boolean} staged - If true, it will set to config.helpers.filters. If false, it will set to config.filters
 * @param {Immutable.List} list - A list of values
 * @returns {Immutable.Map} the updated state
 */
const setFilterState = (state, filter, staged, list) => state.setIn(getFilterPosition(filter, staged), list);
/**
 * Set a value to the results config state
 * @param {Immutable.Map} state - The App State
 * @param {String} key - The Filter name
 * @param {*} value - Any value - string, number, List, Map etc.
 * @returns {Immutable.Map} the updated state
 */
const setResultsConfigValue = (state, key, value) => state.setIn(['config', ...key.split('.')], value);
/**
 *
 * @param {Immutable.List} originalList The original list of filters
 * @param {String|Array} newValue The new value or values to append
 * @returns {Immutable.List} An Immutable List
 */
const appendValueToList = (originalList, newValue) => {
    let filterValue = newValue;
    const newList = originalList;
    if (!Array.isArray(filterValue)) {
        // push a single value to an array so we can destruct it
        filterValue = [filterValue];
    }
    if (newList) {
        // @todo should dedupe too
        return newList.includes(filterValue) ? newList : newList.push(...filterValue);
    }
    return Immutable.List().push(...filterValue);
};
/**
 * Copy data from staged to active filters to apply the filtering on sortedAndFilteredQuotes
 * @param {Immutable.Map} state - The App State
 * @param {String|Boolean} filter The filter name
 * @returns {Immutable.Map} The state
 */
const copyStagedToActiveFilters = (state, filter = false) => {
    const stagedStateFilters = state.getIn(['config', 'helpers', 'filters']);
    if (!stagedStateFilters) {
        throw Error('No staged filters are defined to activate');
    }
    let newState = state;
    if (filter) {
        return setFilterState(newState, filter, false, stagedStateFilters.get(filter));
    }
    stagedStateFilters.keySeq().forEach((key) => {
        newState = setFilterState(newState, key, false, stagedStateFilters.get(key));
    });
    return newState;
};

/**
 * The results reducer to handle quotes and breakpoints.
 * @todo This will need to be factored in future to handle different state objects and quotes may be separate?
 * @param {Object} initialState The initial state of the quote.results object.
 * @param {Object} customCases - A mapping of action type to a function for custom, vertical specific reducer cases
 * @returns {Immutable.Map} New state
 */
const createResultsReducer = (initialState = {}, customCases = {}) => {
    const immutableInitialState = createImmutableMap(initialState);
    return (state = immutableInitialState, action) => {
        switch (action.type) {
            case 'SET_RESULTS_PAYLOAD': {
                // todo how to handle when action.payload does not contain quote or payload - it should fail before it gets to here
                // https://sentry.io/compare-the-market-australia/everest-prod/issues/265268607/
                return state.set('payload', Immutable.fromJS(action.payload.quote.payload));
            }

            /**
             * This action changes the 'features' array back to its original map structure.
             * We perform these operations on a mutable array as we are changing data before
             * converting to an Immutable object.
             * H&C backend 'features' data is now consistent with car, however the H&C results
             * screen for landlords requires a 'features' map structure.
             */
            case 'SET_RESULTS_PAYLOAD_HNC': {
                const plainResultsPayload = action.payload.quote.payload;
                const quotes = plainResultsPayload.results[0].quotes;

                for (let i = 0; i < quotes.length; i += 1) {
                    const features = quotes[i].productMeta.features;

                    if (!(features.size === 0)) {
                        // Transform each object to a key-value pair where the key is the code
                        // and the value is the remainder of the object.
                        const featuresAsMap = features.reduce((acc, feature) => {
                            acc[feature.code] = {
                                code: feature.code,
                                label: feature.label,
                                extra: feature.extra,
                                value: feature.value
                            };
                            return acc;
                        }, {});

                        // Replace 'features' array with map
                        delete plainResultsPayload.results[0].quotes[i].productMeta.features;
                        plainResultsPayload.results[0].quotes[i].productMeta.features = featuresAsMap;
                    }
                }

                const resultsPayload = Immutable.fromJS(plainResultsPayload);
                return state.set('payload', resultsPayload);
            }

            // Get productInfo in journey/load
            case 'SET_PRODUCT_INFO_PAYLOAD': {
                return state.set('productInfo', Immutable.fromJS(action.payload.productInfo));
            }

            case 'GET_MORE_INFO': {
                return state.set('moreInfo', Immutable.fromJS(action.data));
            }

            case 'RESET_RESULTS': {
                return state.set('payload', initialState.get('payload'));
            }

            /**
             * Sets a single key and value to results.config
             */
            case 'SET_RESULTS_CONFIG_VALUE': {
                return setResultsConfigValue(state, action.key, action.value);
            }

            case 'SET_RESULTS_CONFIG_VALUES': {
                let newState = state;
                for (const valueAction of action.actions) {
                    newState = setResultsConfigValue(newState, valueAction.key, valueAction.value);
                }
                return newState;
            }
            /**
             * If a filter is preset to a default value, calling this action will set set the values
             * e.g. to populate all brands at once when results are returned.
             * If the filter is staged, it will also populate the staged store.
             */
            case 'INIT_FILTER_VALUES': {
                const initialised = state.getIn(['config', 'filters', 'initialised', action.filter]);

                if (initialised && !action.force) {
                    return state;
                }

                let newState = state.setIn(['config', 'filters', 'initialised', action.filter], true);

                let filterValues = action.filterValues;
                if (filterValues.size === undefined) {
                    filterValues = Immutable.fromJS(filterValues);
                }

                // Define filter default values if required.
                newState = setFilterState(newState, action.filter, false, filterValues);
                // If this filter can be staged, we should add it here too.
                if (action.filterStaged === true) {
                    newState = setFilterState(newState, action.filter, true, filterValues);
                }
                return newState;
            }

            case 'SET_FILTER_ACTION': {
                const currentActions = state.getIn(['config', 'filters', 'currentFilterActions']).push(action);
                return state.setIn(['config', 'filters', 'currentFilterActions'], currentActions);
            }

            /**
             * Sets a value to the results.config.filters Map for that filter.
             * e.g. action = { filter: 'excess', filterValue: 500 }
             * will result in resultsConfig.filters.excess = 500
             */
            case 'SET_FILTER_VALUE': {
                return setFilterState(state, action.filter, action.filterStaged, action.filterValue);
            }

            /**
             * Add a item to the results.config.filters Map for that filter.
             * e.g. action = { filter: 'brands', filterValue: 'ORIGIN' }
             * will result in resultsConfig.filters.brands = ['ORIGIN','ALINTA_ENERGY']
             */
            case 'SET_FILTER_ARRAY': {
                const originalList = state.getIn(getFilterPosition(action.filter, action.filterStaged));
                const updatedList = appendValueToList(originalList, action.filterValue);
                return setFilterState(state, action.filter, action.filterStaged, updatedList);
            }

            /**
             * Remove a value off a filter from state by filtering the value off the array.
             * e.g. passing in action.filterValue = 'BUDD' it will filter the brands array
             * and remove 'BUDD'
             */
            case 'REMOVE_FILTER_ARRAY': {
                if (Array.isArray(action.filterValue)) {
                    throw Error('This reducer does not currently support iterating through arrays to filter');
                }
                const filterValues = state.getIn(getFilterPosition(action.filter, action.filterStaged));
                const list = filterValues ? filterValues.filter((filterValue) => filterValue !== action.filterValue) : Immutable.List();
                return setFilterState(state, action.filter, action.filterStaged, list);
            }

            /**
             * Activate a staged filter by pushing it into the config.filters object.
             */
            case 'ACTIVATE_STAGED_FILTERS': {
                return copyStagedToActiveFilters(state, action.filter);
            }

            /**
             * Apply sorting state.
             */
            case 'SORT_RESULTS': {
                return state.setIn(
                    ['config', 'sorting'],
                    Immutable.Map({
                        sortBy: action.sortBy,
                        sortDir: action.sortDir,
                        sortType: action.sortType
                    })
                );
            }

            /**
             * Toggle Frequency (Annual / Monthly) Specific Products
             * This is triggered when switching Pricing Frequency on Results, Compare Mode or More Info. Currently relevant for HNC ENSU Month to Month
             * { type: 'TOGGLE_FREQUENCY_SPECIFIC_PRODUCTS', frequency: 'annual'|'monthly' }
             */
            case 'TOGGLE_FREQUENCY_SPECIFIC_PRODUCTS': {
                let newState = state;
                const filterValues = newState.getIn(getFilterPosition('selectedProductIds', true));
                if (filterValues.isEmpty()) {
                    return newState;
                }
                const frequency = newState.getIn(['config', 'filters', 'frequency']);
                const annualPrefix = '_A';
                const monthlyPrefix = '_M';
                const newProductList = filterValues.map((filterValue) => {
                    let newFilterValue = filterValue;
                    newFilterValue = filterValue.includes(annualPrefix) && frequency === 'monthly' ? filterValue.replace(annualPrefix, monthlyPrefix) : newFilterValue;
                    newFilterValue = filterValue.includes(monthlyPrefix) && frequency === 'annual' ? filterValue.replace(monthlyPrefix, annualPrefix) : newFilterValue;
                    return newFilterValue;
                });
                if (newProductList !== filterValues) {
                    // Replace products in staged filters with newProductList (helpers > filters > selectedProductIds)
                    newState = setFilterState(newState, 'selectedProductIds', true, newProductList);
                    // If in compare mode replace features view products with newProductList (config > filters > selectedProductIds)
                    newState = newState.getIn(['config', 'compareModeActive']) ? setFilterState(newState, 'selectedProductIds', false, newProductList) : newState;
                }
                return newState;
            }

            /**
             * Toggle Compare Mode
             * Tried to put this logic back in the components, but it would have resulted in a bit of duplicate logic.
             * { type: 'TOGGLE_COMPARE_MODE', toggle: true|false }
             */
            case 'TOGGLE_COMPARE_MODE': {
                let newState = state;
                const filter = 'selectedProductIds';
                // Toggle Compare Mode
                newState = setResultsConfigValue(newState, 'compareModeActive', action.toggle);
                // If entering compare mode
                if (action.toggle === true) {
                    newState = setResultsConfigValue(newState, 'previousDisplayMode', newState.getIn(['config', 'displayMode']));
                    newState = setResultsConfigValue(newState, 'displayMode', 'column');
                    newState = setResultsConfigValue(newState, 'pageNumber', 1);
                    newState = copyStagedToActiveFilters(newState, filter);
                } else if (action.toggle === false) {
                    // If exiting compare mode
                    // Remove products from compare mode features view (config > filters > selectedProductIds)
                    newState = setFilterState(newState, filter, false, Immutable.List());
                    // Remove products from compare mode basket (helpers > filters > selectedProductIds)
                    if (action.filterStaged === true) {
                        newState = setFilterState(newState, filter, true, Immutable.List());
                    }
                    // Return to previous Display Mode (Quick Price / Features View)
                    const previousMode = newState.getIn(['config', 'previousDisplayMode']);
                    newState = setResultsConfigValue(newState, 'displayMode', previousMode);
                } else {
                    throw Error(`TOGGLE_COMPARE_MODE: Passed invalid toggle value '${action.toggle}'.`);
                }
                return newState;
            }

            /**
             * Toggle a product in or out of selected filters
             * { type: 'TOGGLE_PRODUCT_COMPARE', filterValue: getProductId(props), toggle: true|false }
             * Slightly different case to filters.
             * toggle=true: add the values to the array
             * toggle=false: remove the values from the array and staging
             */
            case 'TOGGLE_COMPARED_PRODUCT': {
                let newState = state;
                const filter = 'selectedProductIds';
                // Add to staged filters
                if (action.toggle) {
                    const originalList = newState.getIn(getFilterPosition(filter, true));
                    const updatedList = appendValueToList(originalList, action.filterValue);
                    newState = setFilterState(newState, filter, true, updatedList);
                } else {
                    const filterValues = newState.getIn(getFilterPosition(filter, true));
                    const list = filterValues ? filterValues.filter((filterValue) => filterValue !== action.filterValue) : Immutable.List();
                    // Remove product from staged filters (helpers > filters > selectedProductIds)
                    newState = setFilterState(newState, filter, true, list);
                    // If in compare mode remove product from selectedProductIds (config > filters > selectedProductIds)
                    newState = newState.getIn(['config', 'compareModeActive']) ? setFilterState(newState, filter, false, list) : newState;
                }
                return newState;
            }

            case 'TOGGLE_ACCORDION': {
                let newState = state;
                // ID is the key of the accordion in the state, this would allow accordions to be opened/closed independently
                // As of now we don't use this property, but it's there for future use
                const { id, isOpen } = action.payload;
                newState = newState.setIn(['config', 'accordionState'], isOpen);

                return newState;
            }

            default: {
                for (const customCase of Object.keys(customCases)) {
                    if (action.type === customCase) {
                        return customCases[customCase](state, action);
                    }
                }
                return state;
            }
        }
    };
};

export { createResultsReducer };
